import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import RoleService from '../../services/role.service';

const RoleList: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await RoleService.getAll();
      setRoles(response.data?.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, type: string) => {
    // Prevent deletion of system roles
    if (type === 'authenticated' || type === 'public') {
      alert('Cannot delete system roles (Authenticated and Public).');
      return;
    }

    if (window.confirm('Are you sure you want to delete this role? Users with this role will need to be reassigned.')) {
      try {
        await RoleService.delete(id);
        fetchRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        alert('Failed to delete role. It may have users assigned to it.');
      }
    }
  };

  const getRoleColor = (type: string) => {
    const colors: any = {
      admin: 'error',
      manager: 'warning',
      accountant: 'info',
      employee: 'default',
      authenticated: 'success',
      public: 'default',
    };
    return colors[type?.toLowerCase()] || 'default';
  };

  const isSystemRole = (type: string) => {
    return type === 'authenticated' || type === 'public';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SecurityIcon fontSize="large" color="primary" />
          <Typography variant="h4">Roles & Permissions</Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/roles/create')}
        >
          New Role
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Roles define what users can do in the system. System roles (Authenticated and Public) cannot be deleted.
      </Alert>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Users</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight="medium">
                        {role.name}
                      </Typography>
                      {isSystemRole(role.type) && (
                        <Chip
                          label="System"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{role.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={role.type}
                      size="small"
                      color={getRoleColor(role.type)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${role.nb_users || 0} user${role.nb_users !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/roles/${role.id}/permissions`)}
                      title="Manage permissions"
                      color="primary"
                    >
                      <VpnKeyIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/roles/${role.id}`)}
                      title="Edit role"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(role.id, role.type)}
                      color="error"
                      disabled={isSystemRole(role.type)}
                      title={
                        isSystemRole(role.type)
                          ? 'Cannot delete system roles'
                          : 'Delete role'
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && roles.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No roles found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RoleList;

